import { GripVertical, Plus, Star, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  type FeaturedOrderType,
  useAutosaveFeaturedOrders,
} from '@/hooks/autosave/useAutosaveFeaturedOrders';
import { useExperiences } from '@/hooks/useExperiences';
import { experienceRegionVenueLine, type ExperienceType } from '@/types/types_experience';

function isFeatured(e: ExperienceType): boolean {
  return typeof e.featuredOrder === 'number';
}

/**
 * Admin page for curating the home page's "Featured Experiences" section:
 * shows the featured experiences in order, supports drag-and-drop reordering,
 * and lets an admin add or remove experiences. Changes persist immediately by
 * renumbering `featuredOrder` (1..N) across the whole list.
 */
export function PageFeaturedList() {
  const { experiences, loading } = useExperiences();
  const saveFeaturedOrders = useAutosaveFeaturedOrders();

  // Local, optimistic ordered list of featured experiences. Seeded from the
  // loaded data and then owned by this component while the user reorders.
  const [featured, setFeatured] = useState<ExperienceType[]>([]);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Re-sync the local list whenever a fresh snapshot arrives (initial load, and
  // after each persist). Adjusting state during render — rather than in an
  // effect — avoids a cascading re-render. `experiences` is a stable reference
  // between renders, so this runs only when the data actually changes.
  const [syncedFrom, setSyncedFrom] = useState(experiences);
  if (syncedFrom !== experiences) {
    setSyncedFrom(experiences);
    setFeatured(experiences.filter(isFeatured).sort((a, b) => a.featuredOrder! - b.featuredOrder!));
  }

  const available = useMemo(
    () =>
      experiences
        .filter((e) => !isFeatured(e))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [experiences],
  );

  // Renumber the given ordered list 1..N and persist. Every featured experience
  // is offered on each save, including the ones that have not moved — the hook
  // works out which positions actually changed and logs only those. The snapshot
  // listener re-syncs local state once the write lands.
  async function persist(orderedFeatured: ExperienceType[], removed?: ExperienceType) {
    setSaving(true);
    const orders: FeaturedOrderType[] = orderedFeatured.map((experience, i) => ({
      experience,
      featuredOrder: i + 1,
    }));
    if (removed) orders.push({ experience: removed, featuredOrder: null });
    try {
      await saveFeaturedOrders(orders);
    } finally {
      setSaving(false);
    }
  }

  function handleDrop(targetIndex: number) {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...featured];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setDragIndex(null);
    setFeatured(next);
    void persist(next);
  }

  function addToFeatured(experience: ExperienceType) {
    const next = [...featured, experience];
    setFeatured(next);
    void persist(next);
  }

  function removeFromFeatured(experience: ExperienceType) {
    const next = featured.filter((e) => e.id !== experience.id);
    setFeatured(next);
    void persist(next, experience);
  }

  return (
    <div className='tw:mx-auto tw:max-w-4xl'>
      <header className='tw:mb-6'>
        <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Featured Experiences</h1>
        <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
          Curate the experiences shown on the public home page. Drag to reorder — the top item shows first.
          {saving && <span className='tw:ml-2 tw:text-ed-gold'>Saving…</span>}
        </p>
      </header>

      {loading ? (
        <p className='tw:py-10 tw:text-center tw:text-ed-stone'>Loading…</p>
      ) : (
        <div className='tw:space-y-8'>
          <section className='tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:p-6 tw:shadow-sm'>
            <h2 className='tw:mb-4 tw:flex tw:items-center tw:gap-2 tw:font-serif tw:text-lg tw:text-ed-black'>
              <Star className='tw:size-4 tw:fill-ed-gold tw:text-ed-gold' /> On the home page
              <span className='tw:text-sm tw:font-normal tw:text-ed-stone'>({featured.length})</span>
            </h2>

            {featured.length === 0 ? (
              <p className='tw:py-8 tw:text-center tw:text-sm tw:text-ed-stone'>
                No featured experiences yet. Add some from the list below.
              </p>
            ) : (
              <ul className='tw:space-y-2'>
                {featured.map((experience, index) => (
                  <li
                    key={experience.id}
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(index)}
                    onDragEnd={() => setDragIndex(null)}
                    className={`tw:flex tw:items-center tw:gap-3 tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-ed-cream/30 tw:p-3 tw:transition-opacity ${
                      dragIndex === index ? 'tw:opacity-40' : ''
                    }`}
                  >
                    <GripVertical className='tw:size-5 tw:shrink-0 tw:cursor-grab tw:text-ed-stone active:tw:cursor-grabbing' />
                    <span className='tw:flex tw:size-7 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-ed-gold tw:text-sm tw:font-medium tw:text-ed-black'>
                      {index + 1}
                    </span>
                    <img
                      src={experience.heroImage}
                      alt={experience.name}
                      className='tw:size-12 tw:shrink-0 tw:rounded-sm tw:object-cover'
                    />
                    <div className='tw:min-w-0 tw:flex-1'>
                      <p className='tw:truncate tw:font-serif tw:text-ed-black'>{experience.name}</p>
                      <p className='tw:truncate tw:text-xs tw:text-ed-stone'>
                        {experienceRegionVenueLine(experience)}
                      </p>
                    </div>
                    <button
                      type='button'
                      title='Remove from featured'
                      onClick={() => removeFromFeatured(experience)}
                      className='tw:shrink-0 tw:rounded-sm tw:border tw:border-ed-umber/30 tw:p-2 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-red tw:hover:text-ed-red'
                    >
                      <X className='tw:size-4' />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className='tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:p-6 tw:shadow-sm'>
            <h2 className='tw:mb-4 tw:font-serif tw:text-lg tw:text-ed-black'>
              Available experiences
              <span className='tw:ml-2 tw:text-sm tw:font-normal tw:text-ed-stone'>({available.length})</span>
            </h2>

            {available.length === 0 ? (
              <p className='tw:py-8 tw:text-center tw:text-sm tw:text-ed-stone'>
                Every experience is already featured.
              </p>
            ) : (
              <ul className='tw:space-y-2'>
                {available.map((experience) => (
                  <li
                    key={experience.id}
                    className='tw:flex tw:items-center tw:gap-3 tw:rounded-sm tw:border tw:border-ed-umber/10 tw:p-3'
                  >
                    <img
                      src={experience.heroImage}
                      alt={experience.name}
                      className='tw:size-12 tw:shrink-0 tw:rounded-sm tw:object-cover'
                    />
                    <div className='tw:min-w-0 tw:flex-1'>
                      <p className='tw:truncate tw:font-serif tw:text-ed-black'>{experience.name}</p>
                      <p className='tw:truncate tw:text-xs tw:text-ed-stone'>
                        {experienceRegionVenueLine(experience)}
                      </p>
                    </div>
                    <button
                      type='button'
                      onClick={() => addToFeatured(experience)}
                      className='tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:rounded-sm tw:bg-ed-gold tw:px-3 tw:py-2 tw:text-sm tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
                    >
                      <Plus className='tw:size-4' /> Feature
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className='tw:text-center tw:text-sm tw:text-ed-stone'>
            Need to edit an experience?{' '}
            <Link to='/admin/experiences' className='tw:text-ed-gold tw:underline'>
              Go to the Experience Database
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}
